import { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { CHANNELS } from "../../data/site";
import { useT } from "@/i18n/I18nContext";

function VideoCard({ video, accent, channelName }) {
  const [active, setActive] = useState(false);
  return (
    <div
      data-testid={`video-card-${video.id}`}
      className="group relative aspect-video overflow-hidden rounded-sm border border-[var(--tt-border)] tt-channel-card"
      style={{ background: "var(--tt-surface)" }}
    >
      {!active ? (
        <button
          aria-label={`Reproduzir ${video.title}`}
          onClick={() => setActive(true)}
          className="absolute inset-0 w-full h-full"
        >
          <img
            src={`https://i.ytimg.com/vi/${video.id}/maxresdefault.jpg`}
            onError={(e) => {
              e.currentTarget.src = `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`;
            }}
            alt={video.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
          <span
            className="absolute top-4 left-4 tt-overline px-2 py-1 rounded-full bg-black/50 backdrop-blur-md"
            style={{ color: accent }}
          >
            {channelName}
          </span>
          <div className="absolute inset-0 grid place-items-center transition-opacity duration-300 opacity-90 group-hover:opacity-100">
            <div
              className="w-16 h-16 rounded-full grid place-items-center border transition-transform duration-500 group-hover:scale-110"
              style={{
                borderColor: accent,
                background: "rgba(0,0,0,0.45)",
                boxShadow: `0 0 36px ${accent}55`,
              }}
            >
              <Play size={22} className="text-white ml-1" fill="white" />
            </div>
          </div>
          <p className="absolute bottom-5 left-5 right-5 text-left font-serif text-lg md:text-xl text-white leading-tight">
            {video.title}
          </p>
        </button>
      ) : (
        <iframe
          className="w-full h-full"
          src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
          title={video.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )}
    </div>
  );
}

export default function FeaturedVideos() {
  const { t } = useT();
  const [active, setActive] = useState(CHANNELS[0].slug);
  const channel = CHANNELS.find((c) => c.slug === active);

  return (
    <section
      id="trabalho"
      data-testid="videos-section"
      className="tt-section relative tt-noise"
      style={{ background: "var(--tt-surface)" }}
    >
      <div className="mx-auto max-w-[1480px] px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div>
            <p className="tt-overline">{t("videos.overline")}</p>
            <h2 className="mt-4 font-serif text-4xl md:text-6xl leading-[1.02] text-[var(--tt-text)]">
              {t("videos.heading_1")}{" "}
              <em className="italic text-[var(--tt-gold)]">{t("videos.heading_2")}</em>.
            </h2>
          </div>

          <div
            role="tablist"
            data-testid="video-tabs"
            className="inline-flex items-center gap-2 p-1 rounded-full border border-[var(--tt-border-strong)] tt-glass self-start md:self-auto"
          >
            {CHANNELS.map((c) => (
              <button
                key={c.slug}
                data-testid={`video-tab-${c.slug}`}
                role="tab"
                aria-selected={active === c.slug}
                onClick={() => setActive(c.slug)}
                className={`relative px-5 py-2.5 rounded-full tt-overline text-[10.5px] transition-colors ${
                  active === c.slug ? "text-[var(--tt-text)]" : "text-[var(--tt-text-muted)] hover:text-[var(--tt-text-dim)]"
                }`}
                style={
                  active === c.slug
                    ? { background: c.accentSoft, boxShadow: `inset 0 0 0 1px ${c.accent}` }
                    : {}
                }
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <motion.div
          key={active}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8"
        >
          {channel.videos.map((v) => (
            <VideoCard key={v.id} video={v} accent={channel.accent} channelName={channel.name} />
          ))}
        </motion.div>

        <div className="mt-14 flex justify-center">
          <a
            href={channel.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="see-more-on-youtube"
            className="tt-btn"
          >
            {t("videos.see_more")} {channel.name}
          </a>
        </div>
      </div>
    </section>
  );
}
